import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Link } from '@grnhse/seedling/lib/birch/typography';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { Label } from '@grnhse/seedling/lib/birch/typography';
import LinkedinActivitySection from 'talent_matching/components/side_panel/linkedin_activity_section';
import { ActionsList } from 'talent_matching/talent_rediscovery/components/table/rediscovery_activity_cell';
import { IconButtonExternalLink } from '@grnhse/seedling/lib/birch/components/buttons';
import PreviewIcon from '@grnhse/seedling/lib/birch/icons/preview';
import { withLocalePrefix } from 'shared/utils/translation';
import { Person, RediscoveryApplication } from 'talent_matching/types';
import { useRediscoveryRowActionsModal } from 'talent_matching/contexts/rediscovery_row_actions_modal_context';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { Row as RowComponent } from 'shared/components/birch/row';
import CandidateChips from 'talent_matching/components/shared/candidate_chips';
import { CandidateName } from './base_side_panel';
import { HeaderLink } from './shared';
import {
  ExpandableMenu,
  EllipsisMenuButton,
  MenuList,
  MenuItem,
} from '@grnhse/seedling/lib/birch/components/expandable_menu';

const t = withLocalePrefix('talent_matching.rediscovery.table.actions');
const tPanel = withLocalePrefix('talent_matching.rediscovery.side_panel');

type Props = {
  application: Person & RediscoveryApplication;
  setLinkedinPreview: (linkedinIframePath: string | null) => void;
};

const RediscoverySidePanelHeader = ({
  application,
  setLinkedinPreview,
}: Props) => {
  const { openTagsFor, openProspectFor, openEmailFor } =
    useRediscoveryRowActionsModal();
  const { linkedinRscEnabled } = useUserContext();

  const profileUrl = `/people/${application.personId}/applications/${application.id}`;

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <RowComponent spacing={1} alignItems={'center'}>
        <HeaderLink>
          <Link href={profileUrl} target="_blank">
            <CandidateName large>
              <span className="pii-blur">{application.fullName}</span>
            </CandidateName>
          </Link>
        </HeaderLink>
      </RowComponent>
      <Stack spacing={2}>
        <CandidateChips application={application} showTags />
        <RowComponent spacing={1}>
          <PrimaryButton onClick={() => openProspectFor(application)} size="md">
            {t('add_prospect')}
          </PrimaryButton>
          <SecondaryButton onClick={() => openTagsFor(application)} size="md">
            {t('tag')}
          </SecondaryButton>
          <IconButtonExternalLink
            icon={PreviewIcon}
            label={t('view_candidate')}
            href={profileUrl}
          />
          <ExpandableMenu>
            <EllipsisMenuButton aria-label={'quick actions'} />
            <MenuList>
              <MenuItem onSelect={() => openEmailFor(application)}>
                {t('send_email')}
              </MenuItem>
            </MenuList>
          </ExpandableMenu>
        </RowComponent>
        {linkedinRscEnabled && (
          <LinkedinActivitySection
            linkedinAddress={application.linkedinAddress}
            onViewLinkedin={
              application.linkedinIframePath
                ? () => {
                    setLinkedinPreview(
                      application.linkedinIframePath as string | null
                    );
                  }
                : undefined
            }
          />
        )}
        {application.rediscoveryActions.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <div style={{ flexShrink: 0 }}>
              <Label>{tPanel('actions_taken')}</Label>
            </div>
            <ActionsList
              actions={application.rediscoveryActions}
              direction="row"
              spacing={1}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default RediscoverySidePanelHeader;
